/**
 * 数据管理主页面
 * 包含侧边栏导航和主内容区域
 */

import React, { useState, useEffect } from 'react';
import { Layout, Button, Space, Avatar, Dropdown, Typography, message } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import PermissionGuard from '../../../components/common/PermissionGuard/PermissionGuard';
import { useMenuDataSimple } from '../../../hooks/useMenuDataSimple';
import { isPathInMenus, findFirstMenuPath } from '../../../utils/menuUtils';
import Sidebar from './components/Sidebar';
import BreadcrumbNav from './components/BreadcrumbNav';
import UserProfileModal from './components/UserProfileModal';
import DataHome from './modules/Home';
import PlatformManagementNew from './modules/Platform/PlatformManagementNew';
import NewsManagement from './modules/News';
import InstitutionManagement from './modules/Institution';
import ShowcaseManagement from './modules/Showcase';
import BusinessManagement from './modules/Business';
import PolicyManagement from './modules/Policy';
import DemandManagement from './modules/Demand';
import CommonConfigManagement from './modules/CommonConfig';
import CarouselManagement from './modules/Carousel';
import UserManagement from './modules/User';
import RoleManagement from './modules/Role';
import MenuManagement from './modules/Menu';
import DictManagement from './modules/Dict';
import LogManagement from './modules/Log';
import PermissionManagement from '../PermissionManagement/PermissionManagement';
import NoPermission from './modules/NoPermission';
import './DataManagement.css';

const { Header, Content } = Layout;
const { Text } = Typography;

const DataManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { loadMenuData, menuData, buildMenuTree, loading: menuLoading } = useMenuDataSimple();
  
  const [collapsed, setCollapsed] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [profileModalVisible, setProfileModalVisible] = useState(false);

  // 移除登录状态变化时的菜单加载逻辑
  // 菜单加载应该由 useMenuDataSimple hook 统一管理
  // 这样可以避免重复调用接口

  // 路径到模块名的映射
  const pathToModuleMap: Record<string, string> = {
    '/data/home': 'home',
    '/data/main': 'home', // 首页路径，映射到home模块
    '/data/platform': 'platform',
    '/data/news': 'news',
    '/data/institution': 'institution',
    '/data/showcase': 'showcase',
    '/data/business': 'business',
    '/data/policy': 'policy',
    '/data/demand': 'demand',
    '/data/common-config': 'common-config',
    '/data/carousel': 'carousel',
    '/data/user': 'user',
    '/data/role': 'role',
    '/data/menu': 'menu',
    '/data/dict': 'dict',
    '/data/log': 'log',
    '/data/permissions': 'permissions',
  };

  // 根据URL路径确定当前模块（从路径中提取模块名）
  // 同时检查用户是否有权限访问当前路径
  useEffect(() => {
    const path = location.pathname;
    
    // 如果访问无权限页面，直接显示
    if (path === '/data/no-permission') {
      setSelectedModule('no-permission');
      return;
    }
    
    // 如果访问 /data 或 /data/，自动跳转到用户有权限的第一个菜单
    if (path === '/data' || path === '/data/') {
      // 等待菜单加载完成
      if (!menuLoading && menuData.length > 0) {
        const menuTree = buildMenuTree(menuData);
        const firstPath = findFirstMenuPath(menuTree);
        if (firstPath) {
          navigate(firstPath, { replace: true });
        } else {
          navigate('/data/no-permission', { replace: true });
        }
      }
      return;
    }
    
    // 检查当前路径是否在用户菜单权限中
    if (!menuLoading && menuData.length > 0) {
      const menuTree = buildMenuTree(menuData);
      const hasPermission = isPathInMenus(path, menuTree);
      
      if (!hasPermission) {
        // 如果没有权限，跳转到第一个有权限的菜单
        const firstPath = findFirstMenuPath(menuTree);
        if (firstPath) {
          navigate(firstPath, { replace: true });
        } else {
          navigate('/data/no-permission', { replace: true });
        }
        return;
      }
    }
    
    // 从路径中提取模块名（支持 /data/moduleName 格式）
    const pathParts = path.split('/').filter(Boolean);
    let moduleName = '';
    
    if (pathParts.length >= 2 && pathParts[0] === 'data') {
      moduleName = pathParts[1];
      // 将 'main' 映射为 'home'（首页路径）
      if (moduleName === 'main') {
        moduleName = 'home';
      }
    } else if (pathParts.length >= 2 && pathParts[0] === 'system') {
      moduleName = pathParts[1];
    }
    
    // 设置当前模块（如果路径包含已知模块名）
    if (moduleName && ['home', 'platform', 'news', 'institution', 'showcase', 'business', 
                       'policy', 'demand', 'common-config', 'carousel', 'user', 'role', 'menu', 
                       'dict', 'log', 'permissions', 'no-permission'].includes(moduleName)) {
      setSelectedModule(moduleName);
    } else {
      // 如果无法识别模块，尝试跳转到第一个有权限的菜单
      if (!menuLoading && menuData.length > 0) {
        const menuTree = buildMenuTree(menuData);
        const firstPath = findFirstMenuPath(menuTree);
        if (firstPath) {
          navigate(firstPath, { replace: true });
        } else {
          navigate('/data/no-permission', { replace: true });
        }
      }
    }
  }, [location.pathname, navigate, menuData, menuLoading, buildMenuTree]);

  // 移除字典加载逻辑：字典数据应该在登录成功后一次性全部加载到本地
  // 这里只需要从缓存读取，不需要调用API

  // 处理模块切换
  const handleModuleChange = (module: string) => {
    setSelectedModule(module);
    navigate(`/data/${module}`);
  };

  // 处理登出
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      message.error('登出失败，请重试');
    }
  };


  // 渲染当前模块内容
  const renderModuleContent = () => {
    switch (selectedModule) {
      case 'home':
        return <DataHome />;
      case 'platform':
        return <PlatformManagementNew />;
      case 'news':
        return <NewsManagement />;
      case 'institution':
        return <InstitutionManagement />;
      case 'showcase':
        return <ShowcaseManagement />;
      case 'business':
        return <BusinessManagement />;
      case 'policy':
        return <PolicyManagement />;
      case 'demand':
        return <DemandManagement />;
      case 'common-config':
        return <CommonConfigManagement />;
      case 'carousel':
        return <CarouselManagement />;
      case 'user':
        return <UserManagement />;
      case 'role':
        return <RoleManagement />;
      case 'menu':
        return <MenuManagement />;
      case 'dict':
        return <DictManagement />;
      case 'log':
        return <LogManagement />;
      case 'permissions':
        return <PermissionManagement />;
      case 'no-permission':
        return <NoPermission />;
      default:
        // 默认显示首页（如果用户有权限）
        return <DataHome />;
    }
  };

  // 处理用户信息查看
  const handleUserInfoClick = () => {
    setProfileModalVisible(true);
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: handleUserInfoClick,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className={`data-management-layout ${collapsed ? 'collapsed' : ''}`}>
      {/* 侧边栏 */}
      <Sidebar 
        collapsed={collapsed}
        selectedModule={selectedModule}
        onModuleChange={handleModuleChange}
      />

      <Layout className="site-layout">
        {/* 顶部导航栏 - 始终显示 */}
        <Header className="site-layout-header">
          <div className="header-left">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
            <BreadcrumbNav className="breadcrumb" />
          </div>
          
          <div className="header-right">
            <Space size="middle">
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                trigger={['hover']}
              >
                <div className="user-info">
                  <Avatar 
                    icon={<span className="user-icon">&#xe6a5;</span>}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                  <Text style={{ marginLeft: 8, color: '#000' }}>
                    {user?.nickname || user?.username || '管理员'}
                  </Text>
                </div>
              </Dropdown>
            </Space>
          </div>
        </Header>

        {/* 主内容区域 - 权限守卫只包裹内容区域 */}
        <Content className="site-layout-content">
          <PermissionGuard requiredPath={location.pathname}>
            {renderModuleContent()}
          </PermissionGuard>
        </Content>
      </Layout>

      {/* 个人资料弹窗 */}
      <UserProfileModal
        visible={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
      />
    </Layout>
  );
};

export default DataManagement;











